import { useState } from "react"
import { Mail } from "lucide-react"

import { BottomSheet } from "@/components/ui/bottom-sheet"
import { ButtonSpinner } from "@/shared/components/button-spinner"
import { cn } from "@/lib/utils"
import { isValidEmail } from "@/shared/lib/email"
import type { ProductPlan } from "@/features/products/types"
import { useCheckout } from "../hooks/use-checkout"

interface ApplePaySheetProps {
  plan: ProductPlan
  email: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

/** Bottom-sheet Apple Pay flow: confirm invoice email, then launch the Apple Pay session. */
export function ApplePaySheet({
  plan,
  email: initialEmail,
  open,
  onClose,
  onSuccess,
}: ApplePaySheetProps) {
  const [email, setEmail] = useState(initialEmail)
  const valid = isValidEmail(email)
  const { status, payWithApplePay } = useCheckout(plan, email, { initCard: false })

  const handlePay = async () => {
    if (!valid) return
    const ok = await payWithApplePay()
    if (ok) onSuccess()
  }

  return (
    <BottomSheet open={open} onClose={onClose}>
      {/* Decorative glow + Apple mark */}
      <div className="relative flex flex-col items-center gap-4 px-4 pb-2 pt-10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-purple-accent/25 to-transparent"
        />
        <svg viewBox="0 0 24 24" className="relative size-14 fill-purple-light" aria-hidden>
          <path d="M17.05 12.18c0-2.06 1.69-3.05 1.77-3.1-0.97-1.42-2.49-1.61-3.03-1.64-1.29-0.13-2.51 0.76-3.16 0.76-0.66 0-1.66-0.74-2.74-0.72-1.41 0.02-2.71 0.82-3.43 2.08-1.46 2.54-0.37 6.29 1.05 8.36 0.7 1.02 1.53 2.16 2.61 2.12 1.05-0.04 1.45-0.68 2.72-0.68 1.27 0 1.62 0.68 2.73 0.66 1.13-0.02 1.84-1.02 2.53-2.05 0.8-1.18 1.13-2.32 1.15-2.38-0.03-0.01-2.21-0.85-2.23-3.36zM15.06 6.42c0.57-0.69 0.96-1.65 0.85-2.61-0.83 0.04-1.83 0.55-2.42 1.24-0.53 0.61-0.99 1.59-0.87 2.53 0.92 0.07 1.87-0.47 2.44-1.16z" />
        </svg>
        <p className="relative text-center text-base text-purple-light/70">
          Please enter your email address so that we can send you an invoice
        </p>
        <div className="relative flex h-12 w-full items-center gap-2 rounded-xl border border-purple-light/20 px-3">
          <Mail className="size-5 shrink-0 text-purple-light/50" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="h-full flex-1 bg-transparent text-base font-medium text-purple-light outline-none placeholder:text-purple-light/40"
          />
        </div>
      </div>

      <div className="p-4 pt-2">
        <button
          type="button"
          onClick={handlePay}
          disabled={!valid || status === "processing"}
          className={cn(
            "flex h-14 w-full items-center justify-center rounded-full text-base font-semibold transition-opacity",
            valid
              ? "bg-gradient-to-b from-peach to-purple-accent text-white hover:opacity-90"
              : "bg-purple-light/10 text-purple-light/30",
          )}
        >
          {status === "processing" ? <ButtonSpinner /> : "Proceed payment"}
        </button>
      </div>
    </BottomSheet>
  )
}
